import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/customer/DashboardLayout";
import useForm from "../../hooks/useForm";
import {
  faUserCircle,
  faEdit,
  faSave,
  faLock, // Añadido para el botón de cambiar contraseña
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { utilsService, DniTypeObject, applyDniPrefix, getMaxLengthFromMask, getDniCompositeKey, findDniTypeByCompositeKey } from "../../services/utils.service";
import { countryService, CountryFull } from "../../services/country.service";
import DashboardCard from "../../components/common/DashboardCard";
import Swal from "sweetalert2";
import "../../styles/app/ProfilePage.css";
import btnStyles from "../../styles/components/Button.module.css";
import { formatPhoneNational, isValidPhone, getPhonePlaceholder } from "../../config/phone.utils";

const ProfilePage: React.FC = () => {
  const { customerData, updateProfile, completeProfile, updatePassword } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [dniTypes, setDniTypes] = useState<DniTypeObject[]>([]);
  const [loadingDniTypes, setLoadingDniTypes] = useState(true);
  const [countryData, setCountryData] = useState<CountryFull | null>(null);
  const [loadingCountry, setLoadingCountry] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false); // Nuevo estado para controlar la visibilidad del formulario de contraseña
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [countryNamesMap, setCountryNamesMap] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const fetchAllCountries = async () => {
      try {
        const countries = await countryService.getAllCountries();
        const map: { [key: string]: string } = {};
        countries.forEach((c) => {
          if (c.iso2Country) {
            map[c.iso2Country.toUpperCase()] = c.name;
          }
        });
        setCountryNamesMap(map);
      } catch (err) {
        console.error("Error fetching country names in ProfilePage:", err);
      }
    };
    fetchAllCountries();
  }, []);

  const getCountryName = (code: string) => {
    if (!code) return "";
    if (countryData && countryData.iso2Country?.toUpperCase() === code.toUpperCase()) {
      return countryData.name;
    }
    return countryNamesMap[code.toUpperCase()] || code;
  };

  // Hook para el formulario de cambio de contraseña
  const {
    values: passwordValues,
    handleChange: handlePasswordChange,
    errors: passwordErrors,
    validate: validatePasswordForm,
    resetForm: resetPasswordForm,
  } = useForm(
    {
      oldPassword: "",
      newPassword: "",
      confirmNewPassword: "",
    },
    {
      oldPassword: (value) => {
        if (customerData?.hasPassword !== false && !value) return "La contraseña actual es requerida.";
        return null;
      },
      newPassword: (value) => {
        if (!value) return "La nueva contraseña es requerida.";
        if (value.length < 6) return "Debe tener al menos 6 caracteres";
        if (!/[a-z]/.test(value)) return "Debe contener al menos una minúscula";
        if (!/[A-Z]/.test(value)) return "Debe contener al menos una mayúscula";
        if (!/[0-9]/.test(value)) return "Debe contener al menos un número";
        if (!/[^A-Za-z0-9]/.test(value))
          return "Debe contener al menos un carácter especial";
        return null;
      },
      confirmNewPassword: (value, allValues) => {
        if (!value) return "Confirme la nueva contraseña.";
        if (value !== allValues.newPassword)
          return "Las contraseñas no coinciden.";
        return null;
      },
    }
  );

  const { values, handleChange, setValues, setErrors, errors, validate } = useForm(
    {
      FirstName: customerData?.FirstName || "",
      LastName: customerData?.LastName || "",
      Email: customerData?.Email || "",
      PhoneNumber: customerData?.PhoneNumber || "",
      KindOfDNI: customerData?.KindOfDNI || (customerData as any)?.kindOfDNI || "",
      DNI: customerData?.DNI || (customerData as any)?.dni || "",
    },
    {
      PhoneNumber: (value) => {
        if (!value) return "El teléfono es requerido.";
        const country = customerData?.ISO2Country || "US";
        if (!isValidPhone(value, country)) return "Número de teléfono inválido.";
        return null;
      },
      KindOfDNI: (value) => {
        if (!value) return "El tipo de documento es requerido.";
        return null;
      },
      DNI: (value, allValues) => {
        if (!value) return "El documento es requerido.";
        const selectedDniObj = findDniTypeByCompositeKey(dniTypes, allValues.KindOfDNI);
        if (selectedDniObj && selectedDniObj.mask) {
          const regex = new RegExp(selectedDniObj.mask);
          if (!regex.test(value)) {
            return `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
          }
        }
        return null;
      }
    }
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const country = customerData?.ISO2Country || "US";
    const formatted = formatPhoneNational(val, country, countryData?.phoneCode);
    setValues((prev) => ({
      ...prev,
      PhoneNumber: formatted,
    }));

    // Validate on keystroke
    const isVal = isValidPhone(formatted, country);
    setErrors((prev) => ({
      ...prev,
      PhoneNumber: !formatted ? "El teléfono es requerido." : !isVal ? "Número de teléfono inválido." : undefined,
    }));
  };

  const handleDniChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const selectedDniObj = findDniTypeByCompositeKey(dniTypes, values.KindOfDNI);
    const formatted = applyDniPrefix(val, selectedDniObj?.mask);
    setValues((prev) => ({
      ...prev,
      DNI: formatted,
    }));

    // Validate on keystroke
    let error: string | null = null;
    if (!formatted) {
      error = "El documento es requerido.";
    } else if (selectedDniObj && selectedDniObj.mask) {
      const regex = new RegExp(selectedDniObj.mask);
      if (!regex.test(formatted)) {
        error = `El documento no coincide con el formato requerido para ${selectedDniObj.kindOfDNI}.`;
      }
    }
    setErrors((prev) => ({
      ...prev,
      DNI: error || undefined,
    }));
  };

  const handleKindOfDniChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setValues((prev) => ({
      ...prev,
      KindOfDNI: val,
      DNI: "",
    }));
    setErrors((prev) => ({
      ...prev,
      KindOfDNI: undefined,
      DNI: undefined,
    }));
  };

  useEffect(() => {
    if (customerData) {
      const country = customerData.ISO2Country || "US";
      setValues({
        FirstName: customerData.FirstName || "",
        LastName: customerData.LastName || "",
        Email: customerData.Email || "",
        PhoneNumber: formatPhoneNational(customerData.PhoneNumber || "", country, countryData?.phoneCode),
        KindOfDNI: customerData.KindOfDNI || (customerData as any).kindOfDNI || "",
        DNI: customerData.DNI || (customerData as any).dni || "",
      });
    }
  }, [customerData, countryData, setValues]);

  useEffect(() => {
    const fetchDniTypes = async () => {
      if (!customerData?.ISO2Country) return;
      try {
        setLoadingDniTypes(true);
        const types = await utilsService.getDniTypes(customerData.ISO2Country);
        setDniTypes(types);

        const rawKindOfDni = customerData.KindOfDNI || (customerData as any).kindOfDNI || "";
        const rawDni = customerData.DNI || (customerData as any).dni || "";
        
        let matchedType: DniTypeObject | undefined = undefined;
        if (customerData.ISO2Country === "VE" && rawDni) {
          const cleanDni = rawDni.trim().toUpperCase();
          matchedType = types.find(t => t.prefix && cleanDni.startsWith(t.prefix.toUpperCase()));
        }
        
        if (!matchedType) {
          matchedType = types.find(t => t.kindOfDNI === rawKindOfDni || getDniCompositeKey(t) === rawKindOfDni);
        }

        if (matchedType) {
          const targetType = matchedType;
          setValues(prev => ({ ...prev, KindOfDNI: getDniCompositeKey(targetType) }));
        }
      } catch (err) {
        console.error("Error al cargar los tipos de DNI.", err);
      } finally {
        setLoadingDniTypes(false);
      }
    };

    fetchDniTypes();
  }, [customerData?.ISO2Country, customerData, setValues]);

  useEffect(() => {
    const fetchCountryData = async () => {
      if (!customerData?.ISO2Country) return;
      try {
        setLoadingCountry(true);
        const country = await countryService.getCountry(customerData.ISO2Country);
        setCountryData(country);
      } catch (err) {
        console.error("Error al cargar el país.", err);
      } finally {
        setLoadingCountry(false);
      }
    };

    fetchCountryData();
  }, [customerData?.ISO2Country]);

  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!validate()) {
      Swal.fire({
        icon: "error",
        title: "Error de Validación",
        text: "Por favor, corrige los errores en el formulario.",
        confirmButtonText: "Ok",
      });
      return;
    }
    setSaving(true);
    try {
      let response;
      if (customerData?.isComplete) {
        // Si el perfil ya está completo, solo actualizamos los campos básicos
        response = await updateProfile(
          values.FirstName,
          values.LastName,
          values.PhoneNumber
        );
      } else {
        // Si el perfil no está completo, lo completamos con todos los datos
        response = await completeProfile(
          values.FirstName,
          values.LastName,
          values.PhoneNumber,
          findDniTypeByCompositeKey(dniTypes, values.KindOfDNI)?.kindOfDNI || values.KindOfDNI,
          values.DNI
        );
      }

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: "Tu perfil ha sido actualizado correctamente.",
          confirmButtonText: "Ok",
        });
        setIsEditing(false);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.error || "Hubo un problema al actualizar tu perfil.",
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.error("Error al guardar el perfil:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error inesperado al guardar el perfil.",
        confirmButtonText: "Ok",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePasswordForm()) {
      Swal.fire({
        icon: "error",
        title: "Error de Validación",
        text: "Por favor, corrige los errores en el formulario.",
        confirmButtonText: "Ok",
      });
      return;
    }

    setSaving(true);
    try {
      const response = await updatePassword(
        passwordValues.oldPassword,
        passwordValues.newPassword
      );

      if (response.success) {
        Swal.fire({
          icon: "success",
          title: "¡Éxito!",
          text: "Tu contraseña ha sido actualizada correctamente.",
          confirmButtonText: "Ok",
        });
        setShowPasswordForm(false);
        resetPasswordForm();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.error || "Hubo un problema al actualizar la contraseña.",
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.error("Error al cambiar la contraseña:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al actualizar la contraseña.",
        confirmButtonText: "Ok",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!customerData) {
    return (
      <DashboardLayout>
        <div>Cargando perfil...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardCard
        icon={faUserCircle}
        title="Mi Perfil"
        description="Actualiza tu información personal y de contacto."
        showVerificationAlerts={true}
      />
      <div className="profile-page-main">
        <div className="profile-page-sidebar">
          <div className="profile-avatar-section">
            <FontAwesomeIcon
              icon={faUserCircle}
              className="profile-avatar-icon"
            />
            <h3>{`${values.FirstName} ${values.LastName}`}</h3>
            <p>{values.Email}</p>
            {customerData?.Code && (
              <p style={{ fontSize: "11px", opacity: 0.7, marginTop: "2px" }}>
                {customerData.Code}
              </p>
            )}
          </div>
          <div className="profile-actions">
            {!showPasswordForm && ( // Mostrar estos botones solo si no estamos en el formulario de contraseña
              <>
                <button
                  onClick={handleEditToggle}
                  className={btnStyles.secondary}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  {isEditing ? "Cancelar" : "Editar Perfil"}
                </button>
                {isEditing && (
                  <button
                    onClick={handleSave}
                    className={btnStyles.primary}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} />
                    {saving ? "Guardando..." : "Guardar Cambios"}
                  </button>
                )}
              </>
            )}
            <button
              onClick={() => {
                setShowPasswordForm(!showPasswordForm);
                setIsEditing(false); // Salir del modo de edición de perfil si se va a cambiar la contraseña
                resetPasswordForm(); // Limpiar el formulario de contraseña al abrir/cerrar
              }}
              className={btnStyles.secondary}
            >
              <FontAwesomeIcon icon={faLock} />
              {showPasswordForm ? "Volver al Perfil" : "Actualizar Contraseña"}
            </button>
          </div>
        </div>
        <div className="profile-page-content">
          {showPasswordForm ? (
            // Formulario de cambio de contraseña
            <form className="profile-form" onSubmit={handleChangePassword}>
              <div className="form-section">
                <h4>Actualizar Contraseña</h4>
                <div className="password-form-grid">
                  {/* Row 1 */}
                  {customerData?.hasPassword !== false ? (
                    <div className="form-group">
                      <label htmlFor="oldPassword">Contraseña Antigua</label>
                      <div className="input-wrapper">
                        <input
                          type={showOldPassword ? "text" : "password"}
                          id="oldPassword"
                          name="oldPassword"
                          value={passwordValues.oldPassword}
                          onChange={handlePasswordChange}
                          className={passwordErrors.oldPassword ? "is-invalid" : ""}
                        />
                        <FontAwesomeIcon
                          icon={showOldPassword ? faEyeSlash : faEye}
                          className="password-toggle-icon"
                          onClick={() => setShowOldPassword(!showOldPassword)}
                        />
                      </div>
                      {passwordErrors.oldPassword && (
                        <p className="invalid-feedback">
                          {passwordErrors.oldPassword}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div></div>
                  )}
                  <div className="form-group">
                    <label htmlFor="newPassword">Nueva Contraseña</label>
                    <div className="input-wrapper">
                      <input
                        type={showNewPassword ? "text" : "password"}
                        id="newPassword"
                        name="newPassword"
                        value={passwordValues.newPassword}
                        onChange={handlePasswordChange}
                        className={passwordErrors.newPassword ? "is-invalid" : ""}
                      />
                      <FontAwesomeIcon
                        icon={showNewPassword ? faEyeSlash : faEye}
                        className="password-toggle-icon"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      />
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="invalid-feedback">
                        {passwordErrors.newPassword}
                      </p>
                    )}
                  </div>

                  {/* Row 2 */}
                  <div></div> {/* Empty cell */}
                  <div className="form-group">
                    <label htmlFor="confirmNewPassword">
                      Verificar Contraseña
                    </label>
                    <div className="input-wrapper">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        id="confirmNewPassword"
                        name="confirmNewPassword"
                        value={passwordValues.confirmNewPassword}
                        onChange={handlePasswordChange}
                        className={
                          passwordErrors.confirmNewPassword ? "is-invalid" : ""
                        }
                      />
                      <FontAwesomeIcon
                        icon={showConfirmPassword ? faEyeSlash : faEye}
                        className="password-toggle-icon"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      />
                    </div>
                    {passwordErrors.confirmNewPassword && (
                      <p className="invalid-feedback">
                        {passwordErrors.confirmNewPassword}
                      </p>
                    )}
                  </div>
                </div>
                <div className="button-container-right">
                  <button
                    type="submit"
                    className={btnStyles.primary}
                    disabled={saving}
                  >
                    <FontAwesomeIcon icon={faSave} />
                    Guardar Nueva Contraseña
                  </button>
                </div>
              </div>
            </form>
          ) : (
            // Formulario de perfil existente
            <form className="profile-form">
              <div className="form-section">
                <h4>Información Personal</h4>
                <div className="personal-info-grid">
                  <div className="form-group">
                    <label htmlFor="FirstName">Nombres</label>
                    <input
                      type="text"
                      id="FirstName"
                      name="FirstName"
                      value={values.FirstName}
                      onChange={handleChange}
                      readOnly={!isEditing}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="LastName">Apellidos</label>
                    <input
                      type="text"
                      id="LastName"
                      name="LastName"
                      value={values.LastName}
                      onChange={handleChange}
                      readOnly={!isEditing}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="Country">País</label>
                    <input
                      type="text"
                      id="Country"
                      name="Country"
                      value={
                        loadingCountry
                          ? "Cargando..."
                          : countryData
                            ? countryData.name
                            : customerData
                              ? getCountryName(customerData.ISO2Country)
                              : ""
                      }
                      readOnly={true}
                      disabled={true}
                    />
                  </div>
                </div>
              </div>
              <div className="form-section">
                <h4>Información de Contacto</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="Email">Correo Electrónico</label>
                    <input
                      type="email"
                      id="Email"
                      name="Email"
                      value={values.Email}
                      onChange={handleChange}
                      readOnly={true}
                      disabled={true}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="PhoneNumber">Teléfono</label>
                    <div className={`phone-input-wrapper ${!isEditing ? "is-readonly" : ""} ${errors.PhoneNumber ? "is-invalid" : ""}`}>
                      {countryData && (
                        <div className="phone-prefix-addon">
                          <img
                            src={`https://flagcdn.com/w40/${countryData.iso2Country.toLowerCase()}.png`}
                            alt={countryData.name}
                            className="phone-flag-img"
                          />
                          <span className="phone-code-text">+{countryData.phoneCode.replace("+", "")}</span>
                        </div>
                      )}
                      <input
                        type="text"
                        id="PhoneNumber"
                        name="PhoneNumber"
                        value={values.PhoneNumber}
                        onChange={handlePhoneChange}
                        readOnly={!isEditing}
                        placeholder={getPhonePlaceholder(countryData?.iso2Country || "US")}
                        className="phone-input-field"
                      />
                    </div>
                    {errors.PhoneNumber && (
                      <p className="invalid-feedback d-block">{errors.PhoneNumber}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="form-section">
                <h4>Información de Identificación</h4>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="KindOfDNI">Tipo de Documento</label>
                    {customerData.isComplete ? (
                      <input
                        type="text"
                        id="KindOfDNI"
                        name="KindOfDNI"
                        value={findDniTypeByCompositeKey(dniTypes, values.KindOfDNI)?.kindOfDNI || values.KindOfDNI}
                        readOnly={true}
                        disabled={true}
                      />
                    ) : (
                      <select
                        id="KindOfDNI"
                        name="KindOfDNI"
                        value={values.KindOfDNI || ""} // Asegura que el valor sea una cadena vacía si no hay valor inicial
                        onChange={handleKindOfDniChange}
                        disabled={!isEditing}
                        className={`form-select ${errors.KindOfDNI ? "is-invalid" : ""}`}
                      >
                        {loadingDniTypes && !values.KindOfDNI ? (
                          <option value="">Cargando...</option> // Opción por defecto mientras carga
                        ) : (
                          <>
                            <option value="" disabled>
                              Seleccione un tipo de DNI
                            </option>{" "}
                            {/* Opción por defecto */}
                            {values.KindOfDNI && !dniTypes.some((t) => t.kindOfDNI === values.KindOfDNI || getDniCompositeKey(t) === values.KindOfDNI) && (
                              <option value={values.KindOfDNI}>
                                {customerData.ISO2Country === 'VE'
                                  ? (findDniTypeByCompositeKey(dniTypes, values.KindOfDNI)?.prefix || values.KindOfDNI)
                                  : (findDniTypeByCompositeKey(dniTypes, values.KindOfDNI)?.kindOfDNI || values.KindOfDNI)}
                              </option>
                            )}
                            {dniTypes.map((type) => (
                              <option key={getDniCompositeKey(type)} value={getDniCompositeKey(type)}>
                                {customerData.ISO2Country === 'VE' ? (type.prefix || type.kindOfDNI) : type.kindOfDNI}
                              </option>
                            ))}
                          </>
                        )}
                      </select>
                    )}
                    {errors.KindOfDNI && (
                      <p className="invalid-feedback d-block">{errors.KindOfDNI}</p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="DNI">Número de Documento</label>
                    <input
                      type="text"
                      id="DNI"
                      name="DNI"
                      value={values.DNI}
                      onChange={handleDniChange}
                      readOnly={!isEditing || customerData.isComplete} // Editable if not complete and in editing mode
                      className={errors.DNI ? "is-invalid" : ""}
                      maxLength={getMaxLengthFromMask(findDniTypeByCompositeKey(dniTypes, values.KindOfDNI)?.mask) || 20}
                    />
                    {errors.DNI && (
                      <p className="invalid-feedback d-block">{errors.DNI}</p>
                    )}
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
